/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css } from 'lit-element';

export const timepickerStyles = css`
  :host {
    outline: 0;
    width: 100%;
    height: 2.5rem;
    position: relative;
    display: flex;
    flex-direction: column;
  }
  /*--Idle state styles--*/
  .timepicker-wrapper {
    width: 100%;
    height: 2.5rem;
    box-sizing: border-box;
    background-color: rgba(var(--ui-1), var(--opacity-7));
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    flex-direction: column;
    border-radius: 0.125rem 0.125rem 0rem 0rem;
    position: relative;
    transition: var(--transition-time) all ease-in-out;
    border-bottom: 0.0625rem solid transparent;
    border-color: rgba(var(--ui-1), var(--opacity-4));
  }
  .timepicker-input {
    display: -webkit-box;
    display: flex;
    flex-direction: row;
    position: relative;
  }
  .timepicker-panel {
    background: var(--base-4);
    min-height: 6.25rem;
    height: 8.5rem;
    position: absolute;
    width: 100%;
    box-sizing: border-box;
    box-shadow: 0.0625rem 0.125rem 0.0625rem var(--shadow-strong),
      -0.0625rem -0.0625rem 0.0625rem var(--shadow-light);
    padding: 1rem 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    border-radius: 0.125rem;
  }

  .timepicker-controls-wrapper {
    flex-direction: row;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .timepicker-controls-wrapper .seperator {
    width: 1.5rem;
  }
  .timepicker-controls {
    text-align: center;
  }
  .control-value {
    margin: 1rem 0;
  }
  :host([error]) .timepicker-wrapper {
    border-color: rgb(var(--functional-red));
  }
  :host([safety]) .timepicker-wrapper {
    border-color: rgb(var(--functional-yellow));
  }
  :host([success]) .timepicker-wrapper {
    border-color: rgb(var(--functional-green));
  }
  .label-area {
    max-width: calc(100% - 2rem);
    margin: 0.5rem 0.5rem 0 0.5rem;
    transition: var(--transition-time) all ease-in-out;
    flex: 1 1 auto;
    height: 2rem;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .label {
    white-space: nowrap;
    overflow: hidden;
    -o-text-overflow: ellipsis;
    text-overflow: ellipsis;
    max-width: calc(100% - 0.625rem);
  }
  .label-slot {
    color: var(--text-secondary);
    font: var(--body-1);
    line-height: 1.5rem;
    -webkit-transition: var(--transition-time) all ease-in-out, 0s line-height ease-in-out,
      0s height ease-in-out;
    -o-transition: var(--transition-time) all ease-in-out, 0s line-height ease-in-out,
      0s height ease-in-out;
    transition: var(--transition-time) all ease-in-out, 0s line-height ease-in-out,
      0s height ease-in-out, 0s width ease-in-out;
    transform-origin: left;
    transform: scale(1);
    position: absolute;
    width: 100%;
    display: flex;
  }
  .close-button {
    opacity: 0;
    height: 1.5rem;
    width: 1.5rem;
    margin: 0.5rem 0.5rem 0.5rem 0rem;
    transition: var(--transition-time) opacity ease-in-out;
    pointer-events: none;
  }
  :host([error]:not([h=''])) .close-button,
  :host([error]:not([m=''])) .close-button,
  :host([error]:not([s=''])) .close-button,
  :host(:hover:not([h=''])) .close-button,
  :host(:hover:not([m=''])) .close-button,
  :host(:hover:not([s=''])) .close-button {
    opacity: 1;
    pointer-events: all;
  }
  .schedule-button {
    margin: 0.5rem 0.5rem 0.5rem 0rem;
    cursor: pointer;
  }
  #dropdown-icon {
    height: 1.5rem;
    width: 1.5rem;
  }
  #closeIcon {
    position: relative;
  }
  /*--Hover styles--*/
  #closeIcon.item-focused {
    opacity: 1;
  }
  :host([active][responsive]:not([error]):not([safety]):not([success])) .timepicker-wrapper,
  :host(:not([responsive]).hasCursor:not([error]):not([safety]):not([success]))
    .timepicker-wrapper {
    border-color: rgba(var(--ui-1), var(--opacity-2));
  }
  :host(:hover) .timepicker-wrapper {
    background: rgba(var(--ui-1), var(--opacity-6));
  }
  :host(.hasCursor) .timepicker-wrapper {
    background-color: rgba(var(--ui-1), var(--opacity-7));
  }
  :host(.hasCursor) .timepicker-wrapper {
    background: rgba(var(--ui-1), var(--opacity-7));
  }
  /*--Focus Styles--*/
  .input-slot {
    opacity: 0;
    position: relative;
    z-index: 2;
    line-height: 1rem;
    height: 1rem;
    display: flex;
    flex-direction: row;
    background: transparent;
    font: var(--body-1);
    color: var(--text-primary);
  }
  .timepicker-panel input::placeholder,
  .time-input-wrapper input::placeholder {
    color: var(--text-disabled);
  }
  .input-slot input {
    height: 1rem;
    width: 1rem;
  }
  :host(.hasCursor) .input-slot,
  :host(.hasValue) .input-slot {
    opacity: 1;
    margin-top: 1rem;
  }
  :host(.hasCursor) .label-area,
  :host(.hasValue) .label-area {
    margin-top: 0.25rem;
  }
  :host(.hasCursor) .label-slot,
  :host(.hasValue) .label-slot {
    font: var(--body-2);
    line-height: normal;
  }
  #h,
  #m,
  #s,
  #h-label,
  #m-label,
  #s-label {
    width: 1rem;
    height: 1rem;
    background: transparent;
    outline: none;
    border: none;
    padding: 0rem;
    font: var(--body-1);
    color: var(--text-primary);
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
    -webkit-transition: all 0.01s ease-in-out;
    -o-transition: all 0.01s ease-in-out;
    transition: all 0.01s ease-in-out;
    overscroll-behavior: contain;
  }
  #m {
    width: 1.5rem;
    text-align: center;
  }
  #h-label,
  #s-label {
    padding: 0rem 0.25rem;
  }
  :host([responsive]) .input-slot input {
    pointer-events: none;
  }
  #amOrPm,
  #ap-label {
    width: 1.25rem;
    height: 1rem;
    background: transparent;
    outline: none;
    padding: 0rem 0.125rem;
    border: none;
    font: var(--body-1);
    color: var(--text-primary);
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
    -webkit-transition: all 0.3s ease-in-out;
    -o-transition: all 0.3s ease-in-out;
    transition: all 0.3s ease-in-out;
    overscroll-behavior: contain;
  }
  #m-label:not(:placeholder-shown),
  #s-label:not(:placeholder-shown) {
    width: 1.5rem;
    padding: 0rem;
    text-align: center;
  }
  #m-label:placeholder-shown {
    width: 1.5rem;
  }
  .seperator {
    width: 0.25rem;
    height: 1rem;
    line-height: 1rem;
    text-align: center;
    color: var(--text-disabled);
  }
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    .input-slot {
      position: relative;
      top: -0.9375rem;
      left: -0.0625rem;
    }
    * {
      -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
      -moz-box-sizing: border-box; /* Firefox, other Gecko */
      box-sizing: border-box;
    }
    #h,
    #m,
    #s,
    #amOrPm,
    #h-label,
    #m-label,
    #s-label {
      padding: 0;
    }
    #amOrPm,
    #ap-label {
      width: 1.375rem;
    }
    :host(.hasValue) .input-slot {
      opacity: 1;
      margin-top: 1.875rem;
    }
  }
  #mandatoryStarSymbol {
    color: rgba(var(--ui-2), var(--opacity-1));
  }
  .seperator.colon-active {
    color: var(--text-primary);
  }
  :host([disabled]) .timepicker-wrapper {
    background-color: rgba(var(--ui-1), var(--opacity-7));
    pointer-events: none;
  }
  :host([disabled]) *,
  :host([disabled]) #h-label,
  :host([disabled]) #m-label,
  :host([disabled]) #s-label,
  :host([disabled]) #ap-label,
  :host([disabled]) input {
    pointer-events: none;
    color: var(--text-disabled);
  }
  :host([disabled]),
  :host([disabled]) .label-slot,
  :host([disabled]) .close-button {
    pointer-events: none;
  }
  :host([disabled]) #mandatoryStarSymbol {
    color: rgba(var(--ui-2), var(--opacity-5)) !important;
  }

  /*Condensed Styles*/
  :host([condensed]) {
    height: 2rem;
  }
  :host([condensed]) .timepicker-wrapper {
    height: 2rem;
  }
  :host([condensed]) .label-area {
    margin: 0.25rem 0.5rem 0.25rem 0.5rem;
    height: auto;
  }
  :host([condensed]) .timepicker-input {
    height: 2rem;
  }
  :host([condensed]) .input-slot {
    margin: 0rem;
    height: 100%;
  }
  :host([error][error-message]),
  :host([hint]) {
    height: auto;
  }
  #time-input-field {
    height: 1rem;
  }
  .time-input-wrapper {
    display: flex;
  }
  :host([condensed]) #time-input-field {
    justify-content: center;
    height: 1.5rem;
  }
  #time-panel {
    height: 0rem;
  }
  :host([condensed]) .input-slot input {
    height: 1rem;
    width: 1rem;
    margin-top: 0.25rem;
  }
  :host([condensed]) .seperator {
    margin-top: 0.1875rem;
  }
  :host([condensed]) .close-button,
  :host([condensed]) .schedule-button {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
  }
  /* helper text */
  .helper-text {
    padding: 0.25rem 0.5rem 0rem 0.5rem;
    box-sizing: border-box;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  :host([disabled]) .helper-text {
    color: var(--text-disabled);
  }
  :host([condensed].hasValue) .label-slot,
  :host([condensed].hasCursor) .label-slot,
  :host([disabled]) .close-button,
  :host([active='false']) .timepicker-panel,
  :host([error][error-message]) .hint {
    display: none;
  }
  :host([error]) .icon-wrapper,
  :host([success]) .icon-wrapper {
    margin: 0.5rem 0.5rem 0.5rem 0rem;
  }
  :host([condensed][error]) .icon-wrapper,
  :host([condensed][success]) .icon-wrapper {
    margin: 0.25rem 0.5rem 0.25rem 0rem;
  }
  :host([no-border]) .timepicker-wrapper {
    border-bottom: none;
  }
  .overlay {
    box-shadow: var(--shadow-overlay);
  }
`;
